import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  useTheme
} from '@mui/material';
import { Release, ReleaseStatus } from '../types';
import ShimmerCard from './ShimmerCard';

// Helper to strip markdown syntax for preview
const stripMarkdown = (text: string): string => {
  return text
    .replace(/#{1,6}\s?/g, '') // Remove headers
    .replace(/\*\*(.+?)\*\*/g, '$1') // Remove bold
    .replace(/\*(.+?)\*/g, '$1') // Remove italic
    .replace(/\[(.+?)\]\(.+?\)/g, '$1') // Remove links
    .replace(/`(.+?)`/g, '$1') // Remove inline code
    .replace(/\n/g, ' ') // Replace newlines with spaces
    .trim();
};

interface RoadmapViewProps {
  releases: Release[];
  onReleaseClick: (release: Release) => void;
  onAddRelease: (status: ReleaseStatus) => void;
  isLoading?: boolean;
}

const RoadmapView: React.FC<RoadmapViewProps> = ({
  releases,
  onReleaseClick,
  onAddRelease,
  isLoading = false
}) => {
  const theme = useTheme();
  const statuses: ReleaseStatus[] = ['Planned', 'Current', 'Past'];

  const getReleasesByStatus = (status: ReleaseStatus) => {
    return releases.filter(release => release.status === status);
  };

  const getColumnColor = (status: ReleaseStatus) => {
    const isDark = theme.palette.mode === 'dark';
    switch (status) {
      case 'Past':
        return isDark ? 'rgba(76, 175, 80, 0.15)' : '#e8f5e9';
      case 'Current':
        return isDark ? 'rgba(33, 150, 243, 0.15)' : '#e3f2fd';
      case 'Planned':
        return isDark ? 'rgba(158, 158, 158, 0.15)' : '#f5f5f5';
      default:
        return isDark ? 'rgba(158, 158, 158, 0.15)' : '#f5f5f5';
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
      {statuses.map((status) => (
        <Box key={status} sx={{ flex: 1, minWidth: { xs: '100%', lg: 'auto' } }}>
          <Paper
            sx={{
              p: { xs: 1.5, sm: 2 },
              backgroundColor: getColumnColor(status),
              minHeight: { xs: '300px', sm: '400px', lg: '500px' }
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">{status}</Typography>
              <Chip label={getReleasesByStatus(status).length} size="small" />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {isLoading ? (
                // Show shimmer cards while loading
                <>
                  <ShimmerCard variant="roadmap" />
                  <ShimmerCard variant="roadmap" />
                  <ShimmerCard variant="roadmap" />
                </>
              ) : (
                getReleasesByStatus(status).map((release) => (
                  <Card key={release.id} elevation={1}>
                    <CardActionArea onClick={() => onReleaseClick(release)}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {release.version}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {(() => {
                            const stripped = stripMarkdown(release.description);
                            return stripped.substring(0, 50) + (stripped.length > 50 ? '...' : '');
                          })()}
                        </Typography>
                        {release.releaseDate && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            {new Date(release.releaseDate).toLocaleDateString()}
                          </Typography>
                        )}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))
              )}

              {/* Hidden: Add Release button
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => onAddRelease(status)}
                fullWidth
              >
                Add Release
              </Button>
              */}
            </Box>
          </Paper>
        </Box>
      ))}
    </Box>
  );
};

export default RoadmapView;
